import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, TrendingUp, Heart, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import { API_BASE_URL } from '../config/api';

// Helper function to get or create user ID
const getOrCreateUserId = () => {
  let storedUserId = localStorage.getItem('tweeporter_user_id');
  if (!storedUserId) {
    storedUserId = 'user_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('tweeporter_user_id', storedUserId);
  }
  return storedUserId;
};

// Check donation bonus status
const checkDonationBonusStatus = () => {
  const bonusData = localStorage.getItem('donation_bonus');
  if (bonusData) {
    try {
      const bonus = JSON.parse(bonusData);
      const now = Date.now();
      const threeDays = 3 * 24 * 60 * 60 * 1000;
      
      if (now - bonus.timestamp < threeDays) {
        console.log('Donation bonus active:', bonus);
        return true;
      } else {
        localStorage.removeItem('donation_bonus');
        return false;
      }
    } catch (e) {
      console.error('Error parsing donation bonus:', e);
      return false;
    }
  }
  return false;
};

const AIUsageLimitTracker = () => {
  const [usageStatus, setUsageStatus] = useState(null);
  // Use lazy initialization
  const [userId] = useState(getOrCreateUserId);
  const [showCreditRequestForm, setShowCreditRequestForm] = useState(false);
  const [requestEmail, setRequestEmail] = useState('');
  const [requestReason, setRequestReason] = useState('');

  // Fetch usage status function
  const fetchUsageStatus = useCallback(async (uid) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/usage-status?user_id=${uid}`
      );
      if (response.ok) {
        const data = await response.json();
        setUsageStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch usage status:', error);
    }
  }, []);

  // Effect for initial data loading - uses AbortController for cleanup
  useEffect(() => {
    const abortController = new AbortController();
    
    // Check donation bonus (synchronous, no state update)
    checkDonationBonusStatus();
    
    // Fetch usage status with abort signal
    const loadUsageStatus = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/usage-status?user_id=${userId}`,
          { signal: abortController.signal }
        );
        if (response.ok) {
          const data = await response.json();
          if (!abortController.signal.aborted) {
            setUsageStatus(data);
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch usage status:', error);
        }
      }
    };
    
    loadUsageStatus();
    
    return () => abortController.abort();
  }, [userId]);

  const handleDonateClick = () => {
    // Note: Bonus will be activated when user returns from PayPal via the thank-you page
    // Return URL should be: https://yourdomain.com/thank-you-donation
    
    // Open PayPal donation page in current window (so it can return to thank-you page)
    // Replace this URL with your actual PayPal donation link
    window.location.href = 'https://www.paypal.com/donate/?hosted_button_id=YOUR_BUTTON_ID';
  };

  const handleCreditRequest = () => {
    if (!requestEmail.trim()) {
      toast({
        title: 'Email Required',
        description: 'Please provide your email address.',
        variant: 'destructive'
      });
      return;
    }

    // In a real implementation, this would send to backend
    console.log('Credit request:', { email: requestEmail, reason: requestReason });
    
    toast({
      title: 'Request Submitted! ✅',
      description: 'We\'ll review your request within 2-4 days and send you an email.',
    });
    
    setShowCreditRequestForm(false);
    setRequestEmail('');
    setRequestReason('');
  };

  const getStatusColor = () => {
    if (!usageStatus) return 'text-slate-600';
    if (usageStatus.status === 'red') return 'text-red-600';
    if (usageStatus.status === 'orange') return 'text-orange-600';
    return 'text-green-600';
  };

  const getStatusBgColor = () => {
    if (!usageStatus) return 'bg-slate-100';
    if (usageStatus.status === 'red') return 'bg-red-50';
    if (usageStatus.status === 'orange') return 'bg-orange-50';
    return 'bg-green-50';
  };

  const getProgressBarColor = () => {
    if (!usageStatus) return 'bg-slate-400';
    if (usageStatus.status === 'red') return 'bg-red-500';
    if (usageStatus.status === 'orange') return 'bg-orange-500';
    return 'bg-green-500';
  };

  if (!usageStatus) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-slate-400 animate-pulse" />
          <span className="text-sm text-slate-600">Loading usage status...</span>
        </div>
      </div>
    );
  }

  const usedPrompts = usageStatus.user_prompts_used || 0;
  const totalPrompts = usageStatus.max_prompts_per_user || 5;
  const remaining = usageStatus.user_prompts_remaining || 0;
  const percentage = ((usedPrompts / totalPrompts) * 100).toFixed(0);

  return (
    <div className={`${getStatusBgColor()} border-2 ${usageStatus.status === 'red' ? 'border-red-200' : usageStatus.status === 'orange' ? 'border-orange-200' : 'border-green-200'} rounded-lg p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertCircle className={`h-5 w-5 ${getStatusColor()}`} />
          <h4 className={`font-semibold ${getStatusColor()}`}>Daily AI Usage</h4>
        </div>
        <span className={`text-sm font-medium ${getStatusColor()}`}>
          {remaining} / {totalPrompts} remaining
        </span>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-white rounded-full h-2.5 mb-3 overflow-hidden">
        <div
          className={`${getProgressBarColor()} h-2.5 transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>

      {/* Status Message */}
      <div className="text-sm space-y-2">
        {usageStatus.status === 'red' ? (
          <p className="text-red-700">
            <strong>Daily limit reached.</strong> Resets at midnight UTC. Thank you for understanding!
          </p>
        ) : usageStatus.status === 'orange' ? (
          <p className="text-orange-700">
            <strong>Running low on prompts.</strong> {remaining} generations left today.
          </p>
        ) : (
          <p className="text-green-700">
            You have <strong>{remaining} AI generations</strong> remaining today.
          </p>
        )}
        
        <div className="text-xs text-slate-600 pt-2 border-t border-slate-200 space-y-2">
          <p>
            💡 <strong>Why limits?</strong> We keep costs low for free users by setting daily limits. Limits reset every 24 hours.
          </p>
          
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <p className="font-semibold text-slate-700 mb-2">🎁 Want more AI generations?</p>
            <p className="mb-3">
              Make a donation (any amount) and get <strong>20 free usages today!</strong>
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDonateClick}
                className="flex-1 bg-rose-500 hover:bg-rose-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Heart className="h-4 w-4" />
                Donate Now
              </button>
              <button
                onClick={() => setShowCreditRequestForm(true)}
                className="flex-1 bg-slate-500 hover:bg-slate-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Send className="h-4 w-4" />
                Request Credits
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Credit requests processed within 2-4 days
            </p>
          </div>
        </div>
      </div>

      {/* Credit Request Form Dialog */}
      <Dialog open={showCreditRequestForm} onOpenChange={setShowCreditRequestForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Additional Credits</DialogTitle>
            <DialogDescription>
              Tell us about your use case and we&apos;ll review your request within 2-4 days.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="request-email">Email Address *</Label>
              <Input
                id="request-email"
                type="email"
                placeholder="your@email.com"
                value={requestEmail}
                onChange={(e) => setRequestEmail(e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="request-reason">Why do you need more credits? (Optional)</Label>
              <textarea
                id="request-reason"
                className="w-full h-24 p-2 border border-slate-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Example: I'm creating educational content for students, building a business demo, etc."
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditRequestForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreditRequest} className="bg-indigo-600 hover:bg-indigo-700">
              <Send className="h-4 w-4 mr-2" />
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIUsageLimitTracker;
